import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { AppScreen } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import type { PatchProfileRequest } from '@/lib/api/apiTypes';
import { getAPIErrorRecoveryCopy, getFirstAPIErrorFieldCopy } from '@/lib/api/apiErrorCopy';
import { useAuth } from '@/lib/auth/auth';
import { EVENT_TAG_GROUPS, getEventTagColors } from '@/features/events/eventTags';
import {
  ACCOUNT_VALIDATION_LIMITS,
  USERNAME_HELP_COPY,
  normalizeUsernameInput,
  validateProfileIdentity,
  validateUsername,
} from '@/features/account/accountValidation';
import { resolvePostSignInHref } from '@/features/account/authRedirect';
import {
  clearProfileSetupDraft,
  readProfileSetupDraft,
  useOnboarding,
  writeProfileSetupDraft,
} from '@/features/onboarding/onboarding';
import {
  trackLocationPermission,
  trackProfileUpdated,
  trackSignUpStepCompleted,
} from '@/lib/analytics/posthog';
import {
  subscribeToAppStateChange,
  subscribeToKeyboardEvent,
} from '@/lib/app/nativeEventSubscriptions';
import { useAvatarUpload } from '@/features/profile/useAvatarUpload';
import { useUpdateProfile } from '@/features/profile/useUpdateProfile';
import { cn } from '@/lib/utils';
import * as ImagePicker from 'expo-image-picker';
import * as Location from 'expo-location';
import { Redirect, router, Stack, useLocalSearchParams } from 'expo-router';
import {
  CameraIcon,
  ExternalLinkIcon,
  MapPinIcon,
  SparklesIcon,
  UserRoundIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type SetupStep = 'profile' | 'interests' | 'location';

const SETUP_STEPS: SetupStep[] = ['profile', 'interests', 'location'];
const STEP_LABELS: Record<SetupStep, string> = { profile: 'Profile', interests: 'Interests', location: 'Location' };
const ALL_INTEREST_TAGS = EVENT_TAG_GROUPS.flatMap((group) => group.tags.map((tag) => tag.value));

export default function ProfileSetupScreen() {
  const params = useLocalSearchParams<{ next?: string }>();
  const postSetupHref = resolvePostSignInHref(params.next);
  const session = useAuth((s) => s.session);
  const request = useAuth((s) => s.request);
  const refreshMe = useAuth((s) => s.refreshMe);
  const syncMarketLocation = useAuth((s) => s.syncMarketLocation);
  const profileSetupPendingUserID = useOnboarding((s) => s.profileSetupPendingUserID);
  const clearProfileSetupPending = useOnboarding((s) => s.clearProfileSetupPending);
  const updateProfileMutation = useUpdateProfile();
  const avatarUploadMutation = useAvatarUpload();

  const existingUsername = session?.me?.profile?.username ?? '';
  const existingDisplayName = session?.me?.profile?.display_name ?? '';
  const existingBio = session?.me?.profile?.bio ?? '';
  const existingAvatarUrl = session?.me?.profile?.avatar_url ?? '';
  const existingInterestTags = session?.me?.profile?.interest_tags ?? [];
  const isPendingProfileSetup = profileSetupPendingUserID === session?.me.user.id;
  const [step, setStep] = React.useState<SetupStep>('profile');
  const [username, setUsername] = React.useState(existingUsername);
  const [displayName, setDisplayName] = React.useState(existingDisplayName);
  const [bio, setBio] = React.useState(existingBio);
  const [avatarUrl, setAvatarUrl] = React.useState(existingAvatarUrl);
  const [interestTags, setInterestTags] = React.useState<string[]>(existingInterestTags);
  const [locationGranted, setLocationGranted] = React.useState(false);
  const [locationNeedsSettings, setLocationNeedsSettings] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(false);
  const [isAvailable, setIsAvailable] = React.useState<boolean | null>(null);
  const [availabilityError, setAvailabilityError] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isDraftHydrating, setIsDraftHydrating] = React.useState(true);
  const [isKeyboardVisible, setIsKeyboardVisible] = React.useState(false);
  const checkTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    readProfileSetupDraft()
      .then((draft) => {
        if (cancelled) {
          return;
        }

        if (!draft) {
          if (isPendingProfileSetup) {
            setUsername('');
            setDisplayName(firstReasonableName(existingDisplayName));
          }
          return;
        }

        setUsername((current) => (isPendingProfileSetup ? draft.username : current || draft.username));
        setDisplayName((current) =>
          isPendingProfileSetup ? draft.displayName : current || draft.displayName
        );
        setBio((current) => (isPendingProfileSetup ? draft.bio : current || draft.bio));
        setAvatarUrl((current) =>
          isPendingProfileSetup ? draft.avatarUrl : current || draft.avatarUrl
        );
        setInterestTags((current) =>
          isPendingProfileSetup || current.length === 0 ? draft.interestTags : current
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsDraftHydrating(false);
        }
      });

    return () => {
      cancelled = true;
      if (checkTimer.current) {
        clearTimeout(checkTimer.current);
      }
    };
  }, [existingDisplayName, isPendingProfileSetup]);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    refreshLocationPermission().catch(() => undefined);

    return subscribeToAppStateChange((state) => {
      if (state === 'active') {
        refreshLocationPermission().catch(() => undefined);
      }
    });
  }, []);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const removeShowSubscription = subscribeToKeyboardEvent(showEvent, () => setIsKeyboardVisible(true));
    const removeHideSubscription = subscribeToKeyboardEvent(hideEvent, () => setIsKeyboardVisible(false));

    return () => {
      removeShowSubscription();
      removeHideSubscription();
    };
  }, []);

  async function refreshLocationPermission() {
    const permission = await Location.getForegroundPermissionsAsync();
    setLocationGranted(permission.status === 'granted');
    setLocationNeedsSettings(permission.status === 'denied' && !permission.canAskAgain);
  }

  React.useEffect(() => {
    if (isDraftHydrating) {
      return;
    }

    if (
      !username.trim() &&
      !displayName.trim() &&
      !bio.trim() &&
      !avatarUrl.trim() &&
      interestTags.length === 0
    ) {
      clearProfileSetupDraft().catch(() => undefined);
      return;
    }

    writeProfileSetupDraft({
      username,
      displayName,
      bio,
      avatarUrl,
      interestTags,
    }).catch(() => undefined);
  }, [avatarUrl, bio, displayName, interestTags, isDraftHydrating, username]);

  React.useEffect(() => {
    if (isDraftHydrating) {
      return;
    }

    const cleaned = username.trim().toLowerCase();
    if (checkTimer.current) {
      clearTimeout(checkTimer.current);
    }

    const usernameError = validateUsername(cleaned);
    if (usernameError) {
      setIsChecking(false);
      setIsAvailable(null);
      setAvailabilityError(
        cleaned.length >= ACCOUNT_VALIDATION_LIMITS.usernameMinLength ? usernameError : null
      );
      return;
    }

    if (
      cleaned === existingUsername &&
      existingUsername.length >= ACCOUNT_VALIDATION_LIMITS.usernameMinLength
    ) {
      setIsChecking(false);
      setIsAvailable(null);
      setAvailabilityError(null);
      return;
    }

    let cancelled = false;
    checkTimer.current = setTimeout(async () => {
      setIsChecking(true);
      try {
        const result = await request<{ available: boolean; error?: string }>(
          `/auth/username-check?username=${cleaned}`
        );
        if (cancelled) {
          return;
        }
        setIsAvailable(result.available);
        setAvailabilityError(result.error ?? null);
      } catch {
        if (cancelled) {
          return;
        }
        setAvailabilityError('Username check is unavailable right now.');
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      if (checkTimer.current) {
        clearTimeout(checkTimer.current);
      }
    };
  }, [existingUsername, isDraftHydrating, request, username]);

  if (!session) {
    return <Redirect href={{ pathname: '/sign-in', params: { next: '/onboarding/profile-setup' } }} />;
  }

  if (isDraftHydrating) {
    return <AppScreen />;
  }

  function handleUsernameChange(value: string) {
    const cleaned = normalizeUsernameInput(value);
    setUsername(cleaned);
    setIsAvailable(null);
    setAvailabilityError(null);
  }

  function toggleInterest(value: string) {
    setInterestTags((current) =>
      current.includes(value) ? current.filter((tag) => tag !== value) : [...current, value]
    );
  }

  function selectAllInterests() {
    setInterestTags(ALL_INTEREST_TAGS);
  }

  async function handlePickAvatar() {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError('Photo access is needed to add a profile picture.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.85,
      allowsEditing: true,
      aspect: [1, 1],
    });

    if (result.canceled || !result.assets?.[0]) {
      return;
    }

    const asset = result.assets[0];
    setError(null);
    try {
      const data = await avatarUploadMutation.mutateAsync({
        uri: asset.uri,
        mimeType: asset.mimeType,
        fileName: asset.fileName,
      });
      setAvatarUrl(data.url);
    } catch {
      setError('Could not upload that photo. Try another image.');
    }
  }

  async function handleEnableLocation() {
    if (locationNeedsSettings) {
      await Linking.openSettings().catch(() => undefined);
      return;
    }

    const permission = await Location.requestForegroundPermissionsAsync().catch(() => null);
    const status = permission?.status ?? 'denied';

    trackLocationPermission({ granted: status === 'granted', source: 'location_onboarding' });

    if (status === 'granted') {
      setLocationGranted(true);
      try {
        const location = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.Balanced,
        });
        await syncMarketLocation(location.coords.latitude, location.coords.longitude);
      } catch {
        // Location permission can be granted before a position is immediately available.
      }
    }

    if (status === 'denied' && permission?.canAskAgain === false) {
      setLocationNeedsSettings(true);
      setError('Location is turned off for Radious. Open Settings to turn it on, or skip for now.');
      return;
    }

    await finishSetup();
  }

  const isOwnUsername =
    username === existingUsername &&
    existingUsername.length >= ACCOUNT_VALIDATION_LIMITS.usernameMinLength;
  const canContinueProfile =
    !isSaving &&
    username.length >= ACCOUNT_VALIDATION_LIMITS.usernameMinLength &&
    displayName.trim().length > 0 &&
    (isAvailable === true || isOwnUsername);
  const allInterestsSelected = interestTags.length === ALL_INTEREST_TAGS.length;

  async function finishSetup() {
    setIsSaving(true);
    setError(null);
    try {
      const trimmedBio = bio.trim();
      const trimmedAvatarUrl = avatarUrl.trim();
      const profileError = validateProfileIdentity({ bio: trimmedBio, displayName, username });
      if (profileError) {
        setError(profileError);
        setIsSaving(false);
        return;
      }

      const payload: PatchProfileRequest = {
        username: username.trim().toLowerCase(),
        display_name: displayName.trim(),
        interest_tags: interestTags,
      };

      if (trimmedBio) {
        payload.bio = trimmedBio;
      }
      if (trimmedAvatarUrl) {
        payload.avatar_url = trimmedAvatarUrl;
      }

      await updateProfileMutation.mutateAsync(payload);
      trackProfileUpdated(['username', 'display_name', 'bio', 'avatar_url', 'interest_tags']);
      trackSignUpStepCompleted('profile');
      await Promise.all([refreshMe(), clearProfileSetupDraft(), clearProfileSetupPending()]);
      router.replace(postSetupHref);
    } catch (error) {
      setError(
        getFirstAPIErrorFieldCopy(error, [
          'username',
          'display_name',
          'bio',
          'avatar_url',
          'interest_tags',
        ]) ??
          getAPIErrorRecoveryCopy(
            error,
            'profile',
            'Could not save your setup. Check your connection and try again.'
          )
      );
      setIsSaving(false);
    }
  }

  return (
    <AppScreen>
      <Stack.Screen options={{ headerShown: false }} />
      <KeyboardAvoidingView
        className="flex-1"
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>

        {/* Pinned top: step progress */}
        <View className="border-b border-border/40 px-6 pb-4 pt-2">
          <SetupProgress step={step} />
        </View>

        {/* Scrollable content */}
        <ScrollView
          className="flex-1"
          automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
          keyboardShouldPersistTaps="handled"
          contentContainerClassName={cn(
            'px-6',
            step === 'profile' && isKeyboardVisible ? 'pt-4 pb-28' : 'pt-8 pb-10'
          )}>

          {step === 'profile' ? (
            <ProfileStep
              availabilityError={availabilityError}
              avatarUrl={avatarUrl}
              bio={bio}
              compact={isKeyboardVisible}
              displayName={displayName}
              isAvailable={isAvailable}
              isChecking={isChecking}
              isOwnUsername={isOwnUsername}
              isUploadingAvatar={avatarUploadMutation.isPending}
              onBioChange={(value) => setBio(value.slice(0, ACCOUNT_VALIDATION_LIMITS.bioMaxLength))}
              onDisplayNameChange={(value) =>
                setDisplayName(value.slice(0, ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength))
              }
              onPickAvatar={() => handlePickAvatar().catch(() => undefined)}
              onUsernameChange={handleUsernameChange}
              username={username}
            />
          ) : null}

          {step === 'interests' ? (
            <InterestsStep
              allSelected={allInterestsSelected}
              interestTags={interestTags}
              onSelectAll={selectAllInterests}
              onToggleInterest={toggleInterest}
            />
          ) : null}

          {step === 'location' ? <LocationStep /> : null}

          {error ? (
            <View className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3">
              <Text variant="small" className="text-destructive">
                {error}
              </Text>
            </View>
          ) : null}
        </ScrollView>

        {step !== 'profile' || !isKeyboardVisible ? (
          <StepFooter
            step={step}
            canContinueProfile={canContinueProfile}
            isSaving={isSaving}
            locationGranted={locationGranted}
            locationNeedsSettings={locationNeedsSettings}
            onProfileContinue={() => setStep('interests')}
            onInterestsContinue={() => setStep('location')}
            onInterestsBack={() => setStep('profile')}
            onLocationEnable={() => handleEnableLocation().catch(() => undefined)}
            onLocationSkip={() => finishSetup().catch(() => undefined)}
            onLocationBack={() => setStep('interests')}
          />
        ) : null}
      </KeyboardAvoidingView>
    </AppScreen>
  );
}

function SetupProgress({ step }: { step: SetupStep }) {
  const currentIndex = SETUP_STEPS.indexOf(step);

  return (
    <View className="items-center gap-2.5">
      <View className="w-full flex-row gap-1.5">
        {SETUP_STEPS.map((item, index) => (
          <View
            key={item}
            className={cn(
              'h-1 flex-1 rounded-full',
              index < currentIndex
                ? 'bg-primary/50'
                : index === currentIndex
                  ? 'bg-primary'
                  : 'bg-muted'
            )}
          />
        ))}
      </View>
      <Text
        variant="small"
        className="text-xs font-semibold uppercase text-primary">
        {STEP_LABELS[step]}
      </Text>
    </View>
  );
}

function StepFooter({
  step,
  canContinueProfile,
  isSaving,
  locationGranted,
  locationNeedsSettings,
  onProfileContinue,
  onInterestsContinue,
  onInterestsBack,
  onLocationEnable,
  onLocationSkip,
  onLocationBack,
}: {
  step: SetupStep;
  canContinueProfile: boolean;
  isSaving: boolean;
  locationGranted: boolean;
  locationNeedsSettings: boolean;
  onProfileContinue: () => void;
  onInterestsContinue: () => void;
  onInterestsBack: () => void;
  onLocationEnable: () => void;
  onLocationSkip: () => void;
  onLocationBack: () => void;
}) {
  const insets = useSafeAreaInsets();

  return (
    <View
      className="gap-2 border-t border-border/40 bg-background px-6 pt-4"
      style={{ paddingBottom: Math.max(insets.bottom, 20) }}>

      {step === 'profile' ? (
        <Button
          size="lg"
          className="h-14 rounded-2xl"
          onPress={onProfileContinue}
          disabled={!canContinueProfile}>
          <Text className="font-bold">Continue</Text>
        </Button>
      ) : null}

      {step === 'interests' ? (
        <>
          <Button size="lg" className="h-14 rounded-2xl" onPress={onInterestsContinue}>
            <Text className="font-bold">Continue</Text>
          </Button>
          <Button variant="ghost" className="h-11" onPress={onInterestsBack}>
            <Text>Back</Text>
          </Button>
        </>
      ) : null}

      {step === 'location' ? (
        <>
          <Button
            size="lg"
            className="h-14 rounded-2xl"
            onPress={locationGranted ? onLocationSkip : onLocationEnable}
            disabled={isSaving}>
            {isSaving ? (
              <ActivityIndicator />
            ) : (
              <View className="flex-row items-center gap-2">
                <Text className="font-bold">
                  {locationGranted ? 'Finish' : locationNeedsSettings ? 'Open Settings' : 'Enable location'}
                </Text>
                {!locationGranted && locationNeedsSettings ? (
                  <Icon as={ExternalLinkIcon} size={15} className="text-primary-foreground" />
                ) : null}
              </View>
            )}
          </Button>
          {!locationGranted ? (
            <Button variant="ghost" className="h-11" onPress={onLocationSkip} disabled={isSaving}>
              <Text>Not now</Text>
            </Button>
          ) : null}
          <Button variant="ghost" className="h-11" onPress={onLocationBack} disabled={isSaving}>
            <Text>Back</Text>
          </Button>
        </>
      ) : null}
    </View>
  );
}

function StepHeader({
  icon,
  title,
  body,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return (
    <View className="gap-5">
      <View className="size-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10">
        <Icon as={icon} className="text-primary" size={26} />
      </View>
      <View className="gap-2">
        <Text variant="h2" className="text-3xl leading-tight">
          {title}
        </Text>
        <Text className="leading-6 text-muted-foreground">{body}</Text>
      </View>
    </View>
  );
}

function ProfileStep({
  availabilityError,
  avatarUrl,
  bio,
  compact,
  displayName,
  isAvailable,
  isChecking,
  isOwnUsername,
  isUploadingAvatar,
  onBioChange,
  onDisplayNameChange,
  onPickAvatar,
  onUsernameChange,
  username,
}: {
  availabilityError: string | null;
  avatarUrl: string;
  bio: string;
  compact: boolean;
  displayName: string;
  isAvailable: boolean | null;
  isChecking: boolean;
  isOwnUsername: boolean;
  isUploadingAvatar: boolean;
  onBioChange: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onPickAvatar: () => void;
  onUsernameChange: (value: string) => void;
  username: string;
}) {
  return (
    <View className={cn(compact ? 'gap-4' : 'gap-8')}>
      {!compact ? (
        <StepHeader
          icon={UserRoundIcon}
          title="Set up your profile"
          body="This is how friends, venues, and hosts will recognize you around Radious."
        />
      ) : null}

      <View className={cn(compact ? 'gap-3' : 'gap-4')}>
        {!compact ? (
          <View className="items-center gap-3">
            <Pressable onPress={onPickAvatar} disabled={isUploadingAvatar}>
              <Avatar
                alt={displayName || username || 'Profile photo'}
                className="size-24 border border-border bg-card">
                {avatarUrl ? <AvatarImage source={{ uri: avatarUrl }} className="rounded-full" /> : null}
                <AvatarFallback className="bg-muted">
                  {isUploadingAvatar ? (
                    <ActivityIndicator />
                  ) : (
                    <Text className="text-2xl font-bold">
                      {getProfileInitials(displayName || username || 'You')}
                    </Text>
                  )}
                </AvatarFallback>
              </Avatar>
              <View
                className="absolute bottom-0 right-0 size-8 items-center justify-center rounded-full border border-background bg-primary">
                <Icon as={CameraIcon} size={15} className="text-primary-foreground" />
              </View>
            </Pressable>
            <Text variant="small" className="text-muted-foreground">
              Add a profile photo
            </Text>
          </View>
        ) : null}

        <View className="gap-2">
          <View
            className={cn(
              'h-14 flex-row items-center rounded-xl border bg-card px-4',
              (isAvailable === true || isOwnUsername) && 'border-emerald-500/70',
              isAvailable === false && 'border-destructive'
            )}>
            <Text className="mr-1 text-muted-foreground/60">@</Text>
            <Input
              value={username}
              onChangeText={onUsernameChange}
              placeholder="yourhandle"
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={ACCOUNT_VALIDATION_LIMITS.usernameMaxLength}
              className="flex-1 border-0 bg-transparent dark:bg-transparent px-0 shadow-none"
            />
            {isChecking ? <ActivityIndicator size="small" /> : null}
            {!isChecking && (isAvailable === true || isOwnUsername) ? (
              <Text variant="small" className="font-semibold text-emerald-500">
                {isOwnUsername ? 'Yours' : 'Available'}
              </Text>
            ) : null}
            {!isChecking && isAvailable === false ? (
              <Text variant="small" className="font-semibold text-destructive">
                Taken
              </Text>
            ) : null}
          </View>
          <Text variant="small" className="text-muted-foreground">
            {username.length}/{ACCOUNT_VALIDATION_LIMITS.usernameMaxLength} · {USERNAME_HELP_COPY}
          </Text>
          {availabilityError ? (
            <Text variant="small" className="text-destructive">
              {availabilityError}
            </Text>
          ) : null}
        </View>

        <Input
          value={displayName}
          onChangeText={onDisplayNameChange}
          placeholder="Your display name"
          autoCapitalize="words"
          maxLength={ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength}
          className="h-14 rounded-xl bg-card"
        />

        <View className="gap-1.5">
          <Input
            value={bio}
            onChangeText={(value) => onBioChange(value.slice(0, ACCOUNT_VALIDATION_LIMITS.bioMaxLength))}
            placeholder="Short bio, vibe, or what you're into (optional)"
            multiline
            maxLength={ACCOUNT_VALIDATION_LIMITS.bioMaxLength}
            className="min-h-24 rounded-xl bg-card py-3"
          />
          <Text variant="small" className="self-end text-muted-foreground/60">
            {bio.length}/{ACCOUNT_VALIDATION_LIMITS.bioMaxLength}
          </Text>
        </View>
      </View>
    </View>
  );
}

function getProfileInitials(value: string) {
  return value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function firstReasonableName(value: string) {
  const cleaned = value.trim().replace(/\s+/g, ' ');
  if (
    !cleaned ||
    cleaned.includes('@') ||
    cleaned.length > ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength
  ) {
    return '';
  }
  return cleaned.split(' ')[0] ?? '';
}

function InterestsStep({
  allSelected,
  interestTags,
  onSelectAll,
  onToggleInterest,
}: {
  allSelected: boolean;
  interestTags: string[];
  onSelectAll: () => void;
  onToggleInterest: (value: string) => void;
}) {
  return (
    <View className="gap-8">
      <StepHeader
        icon={SparklesIcon}
        title="Tune Discover"
        body="Choose what you would actually want to see. Pick a few, or grab everything and narrow it later."
      />

      <View className="flex-row items-center justify-between gap-3">
        <Text variant="small" className="text-muted-foreground">
          {interestTags.length} selected
        </Text>
        <Button variant="outline" size="sm" onPress={onSelectAll} disabled={allSelected}>
          <Text variant="small" className={allSelected ? 'text-muted-foreground' : 'font-semibold'}>
            {allSelected ? 'All selected' : 'Select all'}
          </Text>
        </Button>
      </View>

      <View className="gap-5">
        {EVENT_TAG_GROUPS.map((group) => (
          <View key={group.key} className="gap-3">
            <Text variant="small" className="font-semibold text-muted-foreground">
              {group.label}
            </Text>
            <View className="flex-row flex-wrap gap-2">
              {group.tags.map((tag) => {
                const selected = interestTags.includes(tag.value);
                const color = getEventTagColors(tag.value)[0];
                return (
                  <Pressable
                    key={tag.value}
                    className="flex-row items-center gap-2 rounded-lg border bg-card py-2 pl-2.5 pr-3"
                    style={{
                      borderColor: selected ? color : undefined,
                      backgroundColor: selected ? `${color}22` : undefined,
                    }}
                    onPress={() => onToggleInterest(tag.value)}>
                    <View
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: color, opacity: selected ? 1 : 0.65 }}
                    />
                    <Text
                      variant="small"
                      className={selected ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
                      {tag.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function LocationStep() {
  return (
    <View className="gap-8">
      <StepHeader
        icon={MapPinIcon}
        title="Make nearby feel accurate"
        body="Location keeps Nearby honest and helps Discover start with events, venues, and deals around you."
      />

      <View className="gap-4 rounded-xl border border-border bg-card p-4">
        {[
          { label: 'Nearby', detail: 'sorts events and deals by distance' },
          { label: 'Map', detail: 'shows venues around where you are' },
          { label: 'Discover', detail: 'picks tuned to your location' },
        ].map(({ label, detail }) => (
          <View key={label} className="flex-row gap-3">
            <Text variant="small" className="w-16 font-semibold text-muted-foreground">
              {label}
            </Text>
            <Text variant="small" className="flex-1 text-foreground">
              {detail}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}
